import { useState } from 'react';
import {
  ActionButton,
  Alert,
  Box,
  Button,
  DataTable,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  HairlineCard,
  Icons,
  MenuItem,
  RowAction,
  RowActions,
  Stack,
  TagChip,
  TextField,
  Typography,
  useRowActionsColumn,
} from '@munifocus/ui';
import { useTranslation } from 'react-i18next';
import { FILE_ROLES } from '@munifocus/access';
import {
  useAddFileTeamMember,
  useAppointableUsers,
  useFileTeam,
  useRemoveFileTeamMember,
  useSessionAccess,
} from '../../lib/administration-api';
import { useMutationToast } from '../../lib/mutation-toast';
import { useConfirmAction } from '../../lib/useConfirmAction';
import { useAuth } from '../../providers/AuthProvider';

/**
 * The people on one enforcement file.
 *
 * This is the narrowest scope in the system and the one the brief calls "manage
 * users to files". An officer's appointment says which municipality they may
 * work at; this says which of its files they are actually on, and which one they
 * lead.
 *
 * Only the lead can advance a stage, and a file cannot be left without one —
 * removing the lead is refused, and promoting somebody demotes the incumbent
 * rather than producing two officers who both think they own it.
 */
export function FileTeamPanel({
  fileId,
  localGovernmentId,
}: {
  fileId: string;
  localGovernmentId: string | null;
}) {
  const { t } = useTranslation();
  const actionsColumn = useRowActionsColumn();
  const toast = useMutationToast();
  // Asked at this file, not at the active municipality: the API checks
  // `file:team:manage` against the file, and a file clerk or file supervisor
  // holds it on their seat here without holding it anywhere else.
  const { user } = useAuth();
  const fileAccess = useSessionAccess('file', fileId, user?.id);

  const team = useFileTeam(fileId);
  const candidates = useAppointableUsers(localGovernmentId);
  const add = useAddFileTeamMember(fileId);
  const remove = useRemoveFileTeamMember(fileId);
  const confirm = useConfirmAction();

  const [adding, setAdding] = useState(false);
  const [userId, setUserId] = useState('');
  const [role, setRole] = useState<string>('assisting_officer');

  const manage = fileAccess.data?.permissions.includes('file:team:manage') ?? false;

  /**
   * Resolves whether the action landed, so a dialog can stay open — with what
   * was typed still in it — when it did not. Closing and clearing after a failed
   * save threw the officer's input away behind an error toast.
   */
  const run = async (successKey: string, fn: () => Promise<unknown>): Promise<boolean> => {
    try {
      await fn();
      toast.success(successKey);
      return true;
    } catch (err) {
      toast.error(err, 'gov.admin.errors.actionFailed');
      return false;
    }
  };

  const rows = team.data ?? [];

  return (
    <HairlineCard sx={{ p: 2.5 }}>
      <Stack spacing={1.5}>
        <Box>
          <Typography variant="subtitle2">{t('gov.admin.team.title')}</Typography>
          <Typography variant="caption" color="text.secondary">
            {t('gov.admin.team.help')}
          </Typography>
        </Box>

        <DataTable
          rows={rows}
          getRowKey={(row) => row.id}
          isLoading={team.isLoading}
          emptyLabel={t('gov.admin.team.empty')}
          initialSortKey="person"
          // In the toolbar with every other table's add control, not adrift
          // below the rows where it read as a footnote to them.
          action={
            manage ? (
              <ActionButton
                label={t('gov.admin.team.add')}
                icon={Icons.Plus}
                onClick={() => setAdding(true)}
              />
            ) : undefined
          }
          columns={[
            {
              key: 'person',
              label: t('gov.admin.team.person'),
              primary: true,
              sortValue: (row) => row.userName,
              cell: (row) => (
                <Stack>
                  <Typography variant="body2">{row.userName}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {row.userEmail}
                  </Typography>
                </Stack>
              ),
            },
            {
              key: 'role',
              label: t('gov.admin.team.role'),
              sortValue: (row) => row.role,
              cell: (row) => (
                <TagChip
                  label={t(`gov.admin.roles.${row.role}`)}
                  emphasis={row.role === 'lead_officer'}
                />
              ),
            },
            {
              key: 'grantedBy',
              label: t('gov.admin.team.grantedBy'),
              sortValue: (row) => row.grantedByName ?? '',
              cell: (row) => row.grantedByName ?? '—',
            },
            ...(manage
              ? [
                  {
                    ...actionsColumn,
                    // No remove button on the lead: the API refuses it, and
                    // offering a control that always errors is worse than not
                    // offering one. Reassign instead.
                    cell: (row: (typeof rows)[number]) =>
                      row.role !== 'lead_officer' ? (
                        <RowActions>
                          <RowAction
                            label={t('gov.admin.team.remove')}
                            icon={Icons.Trash2}
                            tone="danger"
                            onClick={async () => {
                              if (
                                !(await confirm({
                                  title: t('gov.admin.team.removeTitle'),
                                  body: t('gov.admin.team.removeBody', { name: row.userName }),
                                  confirmLabel: t('gov.admin.team.remove'),
                                  tone: 'danger',
                                }))
                              ) {
                                return;
                              }
                              await run('gov.admin.team.removed', () => remove.mutateAsync(row.id));
                            }}
                          />
                        </RowActions>
                      ) : null,
                  },
                ]
              : []),
          ]}
        />
      </Stack>

      <Dialog open={adding} onClose={() => setAdding(false)} fullWidth maxWidth="xs">
        <DialogTitle>{t('gov.admin.team.add')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              select
              label={t('gov.admin.team.person')}
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              fullWidth
              required
            >
              {(candidates.data ?? []).map((person) => (
                <MenuItem key={person.id} value={person.id}>
                  {person.displayName} — {person.source}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label={t('gov.admin.team.role')}
              value={role}
              onChange={(e) => setRole(e.target.value)}
              fullWidth
            >
              {FILE_ROLES.map((r) => (
                <MenuItem key={r} value={r}>
                  {t(`gov.admin.roles.${r}`)}
                </MenuItem>
              ))}
            </TextField>
            {role === 'lead_officer' && (
              <Alert severity="warning">{t('gov.admin.team.leadWarning')}</Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdding(false)}>{t('common.actions.cancel')}</Button>
          <Button
            variant="contained"
            disabled={!userId || add.isPending}
            onClick={async () => {
              const ok = await run('gov.admin.team.added', () =>
                add.mutateAsync({ userId, role: role as never }),
              );
              if (!ok) return;
              setAdding(false);
              setUserId('');
            }}
          >
            {t('common.actions.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </HairlineCard>
  );
}
